import { ctaPosture, resolvePrimaryCta } from "../../lib/cta-defaults";
import { heroBusinessFacts, heroFallbackCopy, heroPrimaryText, heroSecondaryCta, hideFieldsBoldIgnores } from "./hero.business";
import type { ComponentConfig } from '@puckeditor/core';
import { Hero } from './hero';
import { HeroFields } from "./hero.fields";
import type { HeroProps } from './hero';
import { ImagePicker } from "../editor/ImagePicker";
import type { JobsiteBusinessContext } from '../../types/runtime-config';
import { RegenerableTextField } from "../editor/RegenerableTextField";
import { getWindowJobsiteConfig } from '../../types/runtime-config';
import { resolveBusinessCtaText } from "../../lib/business-bound-prop";
import { withBusinessCtaOverride } from "../../lib/business-bound-fields";

/**
 * Puck component configuration for Hero
 * Includes fields, defaultProps, and render function
 */

/** What a hero reads from Puck's metadata: the business, this page, the site's pages, and whether it is one page. */
interface HeroMetadata {
  business?: JobsiteBusinessContext;
  onePage?: boolean;
  pagePath?: string;
  pagePaths?: string[];
}

const HeroConfig: ComponentConfig<HeroProps> = {
  defaultProps: {
    backgroundImage:
      "https://imagedelivery.net/a_gggTgujA-YoVa4zU3kuA/c61d7e67-3cb0-401b-0c52-536d2f7f5400/public",
    ctaText: "Get Free Quote",
    ctaUrl: "/contact", // Link to the contact page, not phone
    headline: "", // Let render handle the default
    heroForm: "lead",
    imageMotion: "none",
    imagePosition: "center",
    keywordBadge: "",
    layout: "centered",
    logoImageColor: "original",
    overlay: "dark",
    overlayDarkness: 50,
    photos: [],
    secondaryCtaText: "", // Let render handle the phone CTA
    secondaryCtaUrl: "", // Let render handle the phone URL
    showCopy: "yes",
    showSlideshowDots: "no",
    showSlideshowPause: "no",
    slideSeconds: 6,
    subheadline: "", // Let render handle the default
    textColor: "auto",
    trustIndicators: [
      { text: "Licensed & Insured" },
      { text: "Free Estimates" },
      { text: "Satisfaction Guaranteed" },
    ],
    variant: "clean",
  },
  fields: {
    headline: {
      ...HeroFields.headline,
      contentEditable: true, // Enable inline editing in preview
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.hero.headline"
          label="Headline"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    subheadline: {
      ...HeroFields.subheadline,
      contentEditable: true, // Enable inline editing in preview
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.hero.subheadline"
          label="Subheadline"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    keywordBadge: {
      ...HeroFields.keywordBadge,
      contentEditable: true,
    },
    ctaText: {
      ...HeroFields.ctaText,
      contentEditable: true,
    },
    ctaUrl: HeroFields.ctaUrl,
    secondaryCtaText: HeroFields.secondaryCtaText,
    secondaryCtaUrl: HeroFields.secondaryCtaUrl,
    trustIndicators: HeroFields.trustIndicators,
    backgroundImage: {
      ...HeroFields.backgroundImage,
      render: ({
        value,
        onChange,
      }: {
        value: string | undefined;
        onChange: (val: string | undefined) => void;
      }) => <ImagePicker value={value || ""} onChange={onChange} label="Background Image" />,
    },
    imagePosition: HeroFields.imagePosition,
    imageMotion: HeroFields.imageMotion,
    photos: {
      ...HeroFields.photos,
      arrayFields: {
        ...HeroFields.photos.arrayFields,
        imageUrl: {
          ...HeroFields.photos.arrayFields.imageUrl,
          render: ({
            value,
            onChange,
          }: {
            value?: string;
            onChange: (val: string | undefined) => void;
          }) => <ImagePicker value={value || ""} onChange={onChange} label="Photo" />,
        },
      },
    } as any,
    showCopy: HeroFields.showCopy,
    showSlideshowDots: HeroFields.showSlideshowDots,
    showSlideshowPause: HeroFields.showSlideshowPause,
    slideSeconds: HeroFields.slideSeconds,
    logoImage: {
      ...HeroFields.logoImage,
      render: ({ value, onChange }: { value: string | undefined; onChange: (val: string | undefined) => void }) => (
        <ImagePicker value={value ?? ""} onChange={onChange} label="Featured logo" />
      ),
    },
    logoImageAlt: HeroFields.logoImageAlt,
    logoImageColor: HeroFields.logoImageColor,
    overlay: HeroFields.overlay,
    overlayDarkness: HeroFields.overlayDarkness,
    textColor: HeroFields.textColor,
    layout: HeroFields.layout,
    variant: HeroFields.variant,
    heroForm: HeroFields.heroForm,
  },
  label: "Hero Section",
  render: ({ id, puck, ...props }) => {
    // Access business data from Puck metadata OR window config (fallback)
    const puckMetadata = puck?.metadata as HeroMetadata | undefined,
      windowConfig = getWindowJobsiteConfig(),
      business = puckMetadata?.business || windowConfig?.business,
      businessPhone = business?.phone || business?.telephone,
      // Where the buttons are: this page, the site's pages, and whether the site is one page.
      site = { onePage: puckMetadata?.onePage, pagePath: puckMetadata?.pagePath, pagePaths: puckMetadata?.pagePaths },
      finalCtaText = resolveBusinessCtaText(props.ctaText, business),
      // The owner's CTA card decides where the primary button goes. The saved
      // Prop is a generator default ("/contact" from PAGE_LAYOUTS), not a
      // Choice anyone made, so letting it win produced a "Call Now" button that
      // Opened the contact page.
      primary = resolvePrimaryCta({
        ...site,
        phone: businessPhone,
        phoneFirst: ctaPosture(business, finalCtaText) === "phone",
        savedUrl: props.ctaUrl,
        text: finalCtaText,
      }),
      // The two buttons must offer two different actions.
      secondary = heroSecondaryCta({
        ...site,
        isBold: props.variant === "bold",
        phone: businessPhone,
        primaryCalls: primary.calls,
      }),
      fallbackCopy = heroFallbackCopy(business),
      resolvedProps = {
        ...props,
        headline: props.headline || fallbackCopy.headline,
        subheadline: props.subheadline || fallbackCopy.subheadline,
        ctaText: heroPrimaryText({ calls: primary.calls, isBold: props.variant === "bold", phone: businessPhone, text: primary.text }),
        ctaUrl: primary.url,
        secondaryCtaText: secondary?.text,
        secondaryCtaUrl: secondary?.url,
        variant: props.variant || "clean",
        hasTrackingPhone: Boolean((business)?.hasTrackingPhone),
        // Booking-widget variant of the split-layout side card resolves the same
        // Injected metadata the BookingWidget section uses.
        bookingSlug: business?.slug,
        bookingApiUrl: windowConfig?.apiUrl,
        businessName: business?.name,
        ...heroBusinessFacts(business),
      };

    return <Hero id={id} {...resolvedProps} />;
  },
  /**
   * Only show controls the chosen layout actually reads. A field that does nothing is worse
   * than a missing one: the pro toggles it, sees no change, and stops trusting the editor.
   *
   * Filters the `fields` it is handed rather than rebuilding from HeroFields, so the
   * dashboard's own field overrides (ImageUploader and friends) survive.
   */
  resolveFields: (data, { fields, metadata }) => {
    const props = (data.props || {}) as Partial<HeroProps>,
      layout = props.layout || "centered",
      // The Settings label wins over the saved page value by design — say so rather than
      // Offering a box that silently discards what you type.
      visible = withBusinessCtaOverride(
        { ...(fields as Record<string, unknown>) },
        "ctaText",
        metadata,
      );

    // The side card is a split-layout feature; nothing else renders a form.
    if (layout !== "split") {
      delete visible.heroForm;
    }

    // Alt text and colour only mean something once there is a logo.
    if ((props.logoImage ?? "").trim() === "") {
      delete visible.logoImageAlt;
      delete visible.logoImageColor;
    }

    // Showcase owns the photo rotation and the copy toggle.
    if (layout !== "showcase") {
      delete visible.photos;
      delete visible.showCopy;
      delete visible.showSlideshowDots;
      delete visible.showSlideshowPause;
      delete visible.slideSeconds;
    }

    if (layout === "showcase") {
      // Dots and pacing act on the rotation; with 0–1 photos there is nothing to rotate.
      if ((props.photos || []).filter((photo) => photo?.imageUrl).length < 2) {
        delete visible.showSlideshowDots;
        delete visible.showSlideshowPause;
        delete visible.slideSeconds;
      }
      // Showcase fills the screen with `photos`; the single background image is only its
      // Fallback, and showing both invites the pro to set the one that loses.
      if ((props.photos || []).some((photo) => photo?.imageUrl)) {
        delete visible.backgroundImage;
        delete visible.imagePosition;
      }

      // With the copy hidden there is no text to style, position or colour.
      if (props.showCopy === "no") {
        delete visible.variant;
        delete visible.overlay;
        delete visible.overlayDarkness;
        delete visible.logoImage;
        delete visible.logoImageAlt;
        delete visible.logoImageColor;
        delete visible.textColor;
        delete visible.subheadline;
        delete visible.ctaText;
        delete visible.ctaUrl;
        delete visible.secondaryCtaText;
        delete visible.secondaryCtaUrl;
        delete visible.trustIndicators;
        delete visible.keywordBadge;
      } else if (props.variant !== "proof") {
        // Trust indicators are the proof variant's whole point.
        delete visible.trustIndicators;
      }
    }

    if (props.variant === "bold") {
      hideFieldsBoldIgnores(visible, (metadata as HeroMetadata | undefined)?.business);
    }

    return visible as typeof fields;
  },
};

export { HeroConfig };
